import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";

const slug = (s) => String(s || "").replace(/[^\w]+/g, "_");

/** One exact OFCI product on the project-wide list: quantity breakdown, quantity found and
 *  the optional exact-room split. Rooms are only required when part of the quantity is spread. */
export default function OfciProjectRow({ g, state, setState, open, onToggleOpen }) {
  const id = slug(g.key);
  const picked = !!state?.picked;
  const roomsTotal = Object.values(state?.rooms || {}).reduce((s, v) => s + (Number(v) || 0), 0);
  const qty = state?.notFound ? 0 : Number(state?.qty ?? 0);
  const mismatch = roomsTotal > 0 && Math.abs(roomsTotal - qty) > 1e-9;
  const num = (label, value, cls = "") => (
    <span className="whitespace-nowrap">{label} <b className={`tabular-nums ${cls}`}>{value}</b></span>
  );
  return (
    <li className={`px-2 py-2 ${picked ? "bg-blue-50" : ""}`} data-testid={`ofci-row-${id}`}>
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={picked} disabled={g.unverified <= 0}
               onChange={(e) => setState({ picked: e.target.checked, qty: state?.qty ?? g.unverified })}
               className="mt-1 w-5 h-5 sm:w-4 sm:h-4" data-testid={`ofci-check-${id}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] sm:text-[13px] font-semibold leading-snug">{g.description}</div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[12px] text-gray-600">
            <span>{g.uom}</span>
            {g.scopes.map((s) => <span key={s} className="px-1 rounded bg-blue-50 text-blue-900 text-[10px] font-semibold">{s}</span>)}
            <span>{g.rooms} room(s)</span>
            {g.abs_custody > 0 && (
              <span className="px-1 rounded bg-blue-100 text-blue-900 text-[10px] font-semibold"
                    title="Already moved into ABS warehouse custody on a pull ticket — ABS delivers that quantity, so it is not part of the expected-onsite count"
                    data-testid={`ofci-abs-${id}`}>{g.abs_custody} in ABS custody</span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 text-[12px]" data-testid={`ofci-nums-${id}`}>
            {num("FRS required", g.frs_required)}
            {num("Verified onsite", g.verified, "text-emerald-700")}
            {num("Still unverified", g.unverified, g.unverified > 0 ? "text-amber-800" : "text-emerald-700")}
            {num("Installed", g.installed)}
            {num("Onsite, uninstalled", g.onsite_uninstalled, "text-emerald-700")}
            {g.missing > 0 && num("Reported missing", g.missing, "text-red-700")}
          </div>
          {g.locations.length > 0 && (
            <div className="text-[11px] text-gray-600" data-testid={`ofci-locations-${id}`}>
              Where it is: {g.locations.map((l) => `${l.label} (${l.qty})`).join(" · ")}
            </div>
          )}
          {picked && (
            <div className="mt-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1 text-[12px]">Quantity found
                  <Input type="number" min="0" step="any" value={state?.qty ?? ""} disabled={state?.notFound}
                         className="w-24 min-h-[40px] sm:min-h-[32px] text-right text-[13px]"
                         data-testid={`ofci-qty-${id}`}
                         onChange={(e) => setState({ qty: e.target.value })} />
                </label>
                <label className="flex items-center gap-1.5 text-[12px]">
                  <input type="checkbox" checked={!!state?.notFound} className="w-5 h-5 sm:w-4 sm:h-4"
                         data-testid={`ofci-notfound-${id}`}
                         onChange={(e) => setState({ notFound: e.target.checked, qty: e.target.checked ? 0 : g.unverified })} />
                  Not found onsite
                </label>
                <button type="button" onClick={onToggleOpen} className="text-[12px] text-blue-700 inline-flex items-center gap-1"
                        data-testid={`ofci-rooms-toggle-${id}`}>
                  {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  Exact rooms {roomsTotal > 0 ? `(${roomsTotal} assigned)` : "(optional)"}
                </button>
              </div>
              {qty > g.unverified + 1e-9 && (
                <div className="text-[11px] text-red-700" data-testid={`ofci-over-${id}`}>
                  Only {g.unverified} are still unverified — lower the quantity, or tick "not found onsite".
                </div>
              )}
              {mismatch && (
                <div className="text-[11px] text-red-700" data-testid={`ofci-rooms-mismatch-${id}`}>
                  The room quantities add up to {roomsTotal}, not {qty}. Say exactly which rooms received it.
                </div>
              )}
              {open && (
                <div className="rounded border border-gray-200 divide-y divide-gray-100" data-testid={`ofci-rooms-${id}`}>
                  {g.lines.filter((ln) => ln.unverified > 0).map((ln) => (
                    <div key={ln.line_id} className="flex items-center gap-2 px-2 py-1 text-[12px]">
                      <span className="flex-1 min-w-0 truncate">{ln.room_label}
                        <span className="text-gray-500"> · {ln.scope_type} · expects {ln.unverified}</span>
                      </span>
                      <Input type="number" min="0" step="any" value={state?.rooms?.[ln.room_key] ?? ""}
                             className="w-20 min-h-[36px] sm:min-h-[30px] text-right text-[12px]"
                             data-testid={`ofci-room-qty-${id}-${slug(ln.room_key)}`}
                             onChange={(e) => setState({ rooms: { ...(state?.rooms || {}), [ln.room_key]: e.target.value } })} />
                    </div>
                  ))}
                  <div className="px-2 py-1 text-[11px] text-gray-500">
                    Leave these blank to record it all at one storage location, or fill them in to spread it straight into those rooms.
                  </div>
                </div>
              )}
            </div>
          )}
          {g.verifications.length > 0 && (
            <div className="text-[11px] text-gray-500" data-testid={`ofci-history-${id}`}>
              {g.verifications.length} prior verification(s) — latest {g.verifications[0].qty || 0} by {g.verifications[0].by} at {g.verifications[0].location || "site"} on {g.verifications[0].verified_on || String(g.verifications[0].at || "").slice(0, 10)}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
